import { createFrontendModule } from '@backstage/frontend-plugin-api';
import { ThemeBlueprint } from '@backstage/plugin-app-react';
import {
  createUnifiedTheme,
  genPageTheme,
  palettes,
  UnifiedThemeProvider,
} from '@backstage/theme';
import type { UnifiedThemeOptions } from '@backstage/theme';
import Brightness2Icon from '@material-ui/icons/Brightness2';
import Brightness7Icon from '@material-ui/icons/Brightness7';

/**
 * AnswerRocket brand themes.
 *
 * Deep indigo-blue carries the interface; orange appears only as the warm end
 * of the signature orange -> blue gradient, never on its own for an
 * interactive element (it would read as a warning state). The gradient itself
 * lives in design-system.css, which is where the two visual accents that use
 * it are drawn — MUI has no gradient primitive worth fighting.
 *
 * This file OWNS component styling — every MUI and Backstage component override
 * lives here, and design-system.css no longer styles them. Overrides are keyed
 * on the component name, so they survive the JSS minification that turns
 * `BackstageSidebarItem-root-1` into `jss4-226` in a production build; CSS
 * selectors written against those names match nothing in the deployed image.
 *
 * Component colours reference the --ar-* custom properties rather than the Ramp
 * below, so the tokens in design-system.css stay the single source. The Ramp is
 * still needed for the MUI palette, which has to be real values.
 *
 * answerrocket-bui.css declares the same ramp a third time for Backstage UI,
 * which reads neither this theme nor the --ar-* tokens.
 */

/**
 * The type trio, shared with Control so the two products read as one.
 *
 * Loaded in `packages/app/public/index.html`. This constant previously named
 * Inter, which nothing ever loaded — no link, no @font-face — so the portal has
 * been rendering in the first system fallback since it was written.
 */
const FONT =
  '"Manrope", ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif';

// The mono face is applied in design-system.css by ELEMENT selector, not here.
// Backstage UI's Text and MUI's Typography have no common component for this
// theme to override, and declaring a face in both places is the drift that
// file's own header warns about.

/**
 * The page-header band: brand orange to brand blue.
 *
 * Same pair in both themes on purpose. This is a brand surface, not a themed
 * one — and the dark theme's accent values are lightened derivatives chosen for
 * contrast on near-black, not the brand colours themselves.
 *
 * Kept in sync with --ar-header-gradient in design-system.css, which paints the
 * equivalent band on entity pages. Those use Backstage UI's header, which does
 * not read pageTheme at all.
 */
// Navy -> cyan, Control's pair. The brand's orange -> indigo gradient is the
// marketing mark; carrying it here while every control on the page is cyan
// read as two products sharing a window. Kept as a two-stop band rather than
// Control's animated aurora: this sits behind page titles, where movement
// competes with the text.
const BAND_FROM = '#1d2b6b';
const BAND_TO = '#1f97b5';

/**
 * Headings share one tightening; the scale itself is MUI's.
 *
 * Manrope carries weight where the serif could not — with one face doing both
 * headings and body, weight and tracking are the whole hierarchy, so headings
 * are heavier than they were and tighter than body text.
 *
 * The FACE is applied in design-system.css by element selector, because
 * Backstage UI's Text has no MUI component for this theme to override. Weight
 * and tracking stay here, so MUI headings have exactly one source.
 */
const HEADING = { letterSpacing: '-0.018em', fontWeight: 700 };

/**
 * Zero elevation at every level. MUI emits elevation0…elevation24 as separate
 * rules, so flattening shadows means naming all of them rather than setting
 * boxShadow once on `root` — which they would each override.
 */
const ELEVATIONS = Object.fromEntries(
  Array.from({ length: 25 }, (_, i) => [
    `elevation${i}`,
    { boxShadow: 'none' },
  ]),
);

/** Every filter label reads the same, whichever control it belongs to. */
const MICRO_LABEL = {
  fontSize: '0.6875rem',
  fontWeight: 600,
  letterSpacing: '0.06em',
  textTransform: 'uppercase' as const,
  marginBottom: 7,
};

/**
 * Overrides whose component names TypeScript does not know about.
 *
 * @backstage/plugin-catalog and @backstage/plugin-catalog-graph publish their
 * rule names as ClassKey types and augment the Material UI v4
 * `BackstageOverrides` type, but not the v5 `Components` map that
 * UnifiedThemeOptions is typed against — so these keys fail the object-literal
 * check while CatalogReact* ones pass. They resolve at runtime exactly like
 * every other named component; this is a typing gap, not a behavioural one, and
 * the cast is scoped to just the affected keys.
 */
const UNTYPED_OVERRIDES = {
  // The "Owned components (n)" heading: a quiet section label, not a page
  // title. Rule names are `root | text` — the heading is `text`.
  PluginCatalogTableToolbar: {
    styleOverrides: {
      root: { '&&': { padding: '14px 16px', minHeight: 0 } },
      text: {
        '&&': {
          fontSize: '0.9375rem',
          fontWeight: 600,
          letterSpacing: '-0.01em',
        },
      },
    },
  },
  // Dependency graph. Stock nodes are solid bright-blue slabs with white text
  // and read as a different product; make them surfaces like everything else
  // and reserve the accent for the focused entity.
  PluginCatalogGraphCustomNode: {
    styleOverrides: {
      // The component colours nodes through `&.primary` / `&.secondary` inside
      // its own `node` rule, which outranks a bare `node` override — so those
      // are named too. `&&` puts this a step above either. Focused comes last:
      // a node can be both, and the accent should win.
      node: {
        rx: 6,
        ry: 6,
        strokeWidth: 1,
        '&&, &&.primary, &&.secondary': {
          fill: 'var(--ar-surface-2)',
          stroke: 'var(--ar-border)',
        },
        '&&.focused': {
          fill: 'var(--ar-accent-soft)',
          stroke: 'var(--ar-accent)',
        },
      },
      text: { '&&': { fill: 'var(--ar-fg)', fontSize: 12 } },
    },
  },
  // Relation labels on the edges: supporting detail, not headline.
  PluginCatalogGraphCustomLabel: {
    styleOverrides: {
      text: { fill: 'var(--ar-fg-3)', fontSize: 11 },
      secondary: { fill: 'var(--ar-fg-3)' },
    },
  },
} as UnifiedThemeOptions['components'];

/** Icon rail: sized to the icon, not to a label that is no longer rendered. */
/**
 * A labelled rail. It was icon-only (56px) until the UI sweep: a dozen
 * unlabelled glyphs asked people who visit monthly to remember what a layers
 * icon or a checklist icon meant, and the labels were in the DOM anyway.
 */
const RAIL_WIDTH = 224;
const RAIL_TILE = 40;
const RAIL_GUTTER = 10;

type Ramp = {
  bg: string;
  surface: string;
  surfaceHover: string;
  sidebar: string;
  border: string;
  borderStrong: string;
  fg: string;
  fgMuted: string;
  fgSubtle: string;
  accent: string;
  accent2: string;
  accentHover: string;
  accentSoft: string;
  accentSoftHover: string;
  onAccent: string;
};

// Both ramps are Control's, verbatim (control/app/globals.css). A value here
// that differs from that file is a bug, not a decision — see the note at the
// top of design-system.css.
const DARK: Ramp = {
  bg: '#060912',
  surface: '#0c1124',
  surfaceHover: '#121935',
  sidebar: '#04071a',
  border: 'rgba(216,226,255,0.10)',
  borderStrong: 'rgba(216,226,255,0.20)',
  fg: '#e9edfb',
  fgMuted: '#939bc0',
  fgSubtle: '#6f77a0',
  // Control's signature cyan, lightened for near-black exactly as Control
  // does. Note onAccent: cyan is a LIGHT accent, so the ink on a filled
  // button is dark — white on it fails contrast, unlike the indigo it
  // replaced.
  accent: '#3fc4e0',
  accent2: '#34d6c1',
  accentHover: '#6ad5ec',
  accentSoft: 'rgba(63,196,224,0.16)',
  accentSoftHover: 'rgba(63,196,224,0.24)',
  onAccent: '#04071a',
};

const LIGHT: Ramp = {
  bg: '#f5f6fa',
  surface: '#ffffff',
  surfaceHover: '#f1f3f9',
  sidebar: '#ffffff',
  border: 'rgba(15,22,56,0.09)',
  borderStrong: 'rgba(15,22,56,0.16)',
  // Control's navy ink. Deep navy rather than black — the brand's heading
  // colour, and easier to read at length.
  fg: '#0f1638',
  fgMuted: '#5a6080',
  fgSubtle: '#858ba0',
  accent: '#1f97b5',
  accent2: '#0fb6a2',
  accentHover: '#17798f',
  accentSoft: 'rgba(31,151,181,0.10)',
  accentSoftHover: 'rgba(31,151,181,0.16)',
  onAccent: '#ffffff',
};

const buildTheme = (mode: 'light' | 'dark', c: Ramp) => {
  // genPageTheme composes `${shape},  linear-gradient(90deg, ...colors)` into
  // theme.page.backgroundImage, and Header paints that plus theme.page.fontColor
  // on its title and subtitle (subtitle at opacity 0.8). So the band, its
  // gradient and its text colour are all the framework's own mechanism — no CSS
  // override needed.
  //
  // `shape: 'none'` is the point: shape is the decorative burst/wave PATTERN
  // laid over the gradient, and it is what made the stock header read as
  // Backstage. 'none' is a valid background-image layer, so the gradient is
  // left on its own.
  const page = genPageTheme({
    colors: [BAND_FROM, BAND_TO],
    shape: 'none',
    options: { fontColor: '#ffffff' },
  });

  return createUnifiedTheme({
    fontFamily: FONT,
    palette: {
      ...(mode === 'dark' ? palettes.dark : palettes.light),
      primary: { main: c.accent },
      secondary: { main: c.accent },
      background: { default: c.bg, paper: c.surface },
      // Headings used to be coloured by a blanket !important rule in
      // design-system.css. That rule also beat theme.page.fontColor, which is
      // applied as a plain class, so the title on the brand band came out dark
      // in light mode. Carrying it on the palette instead lets the page theme
      // win where it should.
      text: { primary: c.fg, secondary: c.fgMuted },
      link: c.accent,
      linkHover: c.accentHover,
      navigation: {
        ...(mode === 'dark'
          ? palettes.dark.navigation
          : palettes.light.navigation),
        background: c.sidebar,
        indicator: 'transparent', // no left bar; selected state is an inset pill
        color: c.fgMuted,
        selectedColor: c.fg,
        navItem: { hoverBackground: c.surfaceHover },
        submenu: { background: c.sidebar },
      },
    },
    defaultPageTheme: 'home',
    pageTheme: {
      home: page,
      documentation: page,
      service: page,
      tool: page,
      other: page,
      app: page,
      apis: page,
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: { body: { fontFamily: FONT, backgroundColor: c.bg } },
      },
      // Labelled rail: a full-width row per item, icon in a fixed 40px slot
      // so labels line up, and the selected state a rounded tile across the
      // row rather than Backstage's 3px left bar.
      BackstageSidebarItem: {
        styleOverrides: {
          root: {
            minHeight: RAIL_TILE,
            height: RAIL_TILE,
            width: `${RAIL_WIDTH - RAIL_GUTTER * 2}px !important`,
            minWidth: `${RAIL_WIDTH - RAIL_GUTTER * 2}px !important`,
            margin: `2px ${RAIL_GUTTER}px`,
            borderRadius: 10,
            paddingLeft: 0,
            justifyContent: 'flex-start',
            '&:hover': { backgroundColor: c.surfaceHover },
          },
          // `open` and `closed` both set width after `root` in the component's
          // own stylesheet, so a root-only override loses to them. This is why
          // the rail once stayed the wrong width in production.
          open: {
            width: `${RAIL_WIDTH - RAIL_GUTTER * 2}px !important`,
            minWidth: `${RAIL_WIDTH - RAIL_GUTTER * 2}px !important`,
          },
          // Unpinned (Settings › Pin Sidebar off) the rail is Backstage's
          // closed width, 72px, with no labels: a centred icon tile.
          closed: {
            width: `${RAIL_TILE}px !important`,
            minWidth: `${RAIL_TILE}px !important`,
            margin: `2px ${(72 - RAIL_TILE) / 2}px !important`,
            justifyContent: 'center',
          },
          label: {
            fontSize: '0.875rem',
            fontWeight: 560,
            letterSpacing: '-0.006em',
            whiteSpace: 'nowrap',
          },
          // Backstage sizes this to iconContainerWidth (72px, its own closed
          // rail); a fixed tile keeps every label on one left edge.
          iconContainer: {
            width: `${RAIL_TILE}px !important`,
            minWidth: `${RAIL_TILE}px !important`,
            height: RAIL_TILE,
            display: 'flex !important',
            alignItems: 'center !important',
            justifyContent: 'center !important',
            marginRight: 4,
          },
          selected: {
            // The component draws its selected indicator as
            // borderLeft: solid 3px navigation.indicator, which would nudge
            // the selected row right. The tile IS the indicator here.
            borderLeft: 'none !important',
            backgroundColor: c.accentSoft,
            color: c.accent,
            '&:hover': { backgroundColor: c.accentSoftHover },
          },
        },
      },
      // The create wizard's stepper (scaffolder-react renders MUI's Stepper
      // even with the form in BUI). After the house kit's Steps: the accent
      // marker for the current step, a quiet ring for the ones ahead, and
      // labels in the app's weight rather than MUI's caption grey.
      MuiStepIcon: {
        styleOverrides: {
          root: {
            width: 26,
            height: 26,
            color: c.surfaceHover,
            '&$active': { color: c.accent },
            '&$completed': { color: c.accent },
            // A step ahead is a pale disc, so its number needs ink, not the
            // on-accent white the current step uses.
            '&:not($active) $text': { fill: c.fgMuted },
          },
          text: {
            fontFamily: 'var(--ar-font-mono)',
            fontSize: '0.6875rem',
            fontWeight: 600,
            fill: c.onAccent,
          },
          active: {},
          completed: {},
        },
      },
      MuiStepLabel: {
        styleOverrides: {
          label: {
            fontSize: '0.8125rem',
            fontWeight: 520,
            color: c.fgSubtle,
            '&$active': { color: c.fg, fontWeight: 650 },
            '&$completed': { color: c.fgMuted, fontWeight: 560 },
          },
          active: {},
          completed: {},
        },
      },
      MuiStepConnector: {
        styleOverrides: { line: { borderColor: c.border } },
      },
      // Flat, subtly bordered surfaces instead of heavy elevation. boxShadow on
      // `root` rather than per-elevation so AppBar and every elevation level are
      // covered by one rule; Card is a Paper and needs no override of its own.
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundColor: 'var(--ar-surface)',
            backgroundImage: 'none',
            border: '1px solid var(--ar-border-soft)',
            // A Paper inside a Paper reads as one surface, not a box in a box.
            '& [class*="MuiPaper-root"]': {
              border: 'none',
              backgroundColor: 'transparent',
              borderRadius: 0,
            },
          },
          // `rounded` and `elevationN` are separate rules emitted AFTER `root`
          // in the component's own stylesheet, so a root-only override loses to
          // them at equal specificity. They have to be named directly — this is
          // what the blanket !important in design-system.css was really for.
          rounded: { borderRadius: 'var(--ar-radius-lg)' },
          ...ELEVATIONS,
        },
      },
      MuiTypography: {
        styleOverrides: {
          // Colour deliberately omitted: it would apply to the page-header title
          // too and beat theme.page.fontColor, painting it dark on the brand
          // band. Heading colour comes from palette.text.primary.
          h1: HEADING,
          h2: HEADING,
          h3: HEADING,
          h4: HEADING,
          h5: HEADING,
          h6: HEADING,
          body2: { color: 'var(--ar-fg-2)' },
        },
      },
      // `&&` doubles the generated class, lifting these to specificity (0,2,0)
      // so they beat MuiTableCell-sizeSmall and material-table's own padding
      // rule, both of which are plain single classes emitted later.
      MuiTableCell: {
        styleOverrides: {
          head: {
            '&&': {
              fontSize: '0.6875rem',
              fontWeight: 600,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              padding: '10px 16px',
              borderBottom: '1px solid var(--ar-border)',
              borderTop: 'none',
              background: 'transparent',
              // The catalog table sets colour as an INLINE style on the header
              // cell, and nothing but !important beats an inline style. This is
              // the one place it is unavoidable — scoped to one property rather
              // than the blanket rules this replaced.
              color: 'var(--ar-fg-3) !important',
            },
          },
          body: {
            '&&': {
              fontSize: '0.875rem',
              color: 'var(--ar-fg)',
              padding: '11px 16px',
              borderBottom: '1px solid var(--ar-border-soft)',
            },
            // Links in a table cell are not MuiLink, so they need their own rule.
            '& a': {
              color: 'var(--ar-accent)',
              textDecoration: 'none',
              fontWeight: 500,
            },
            '& a:hover': { color: 'var(--ar-accent-hover)' },
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          // The cell paints over the row, so the hover tint has to land on the
          // cell rather than the row itself.
          root: {
            '&:hover > [class*="MuiTableCell-body"]': {
              backgroundColor: 'var(--ar-surface-2)',
            },
          },
        },
      },
      // Last row shouldn't draw a divider into empty space.
      MuiTableBody: {
        styleOverrides: {
          root: {
            '& [class*="MuiTableRow-root"]:last-child > [class*="MuiTableCell-body"]':
              { borderBottom: 'none' },
          },
        },
      },
      MuiLink: {
        styleOverrides: {
          root: {
            color: 'var(--ar-accent)',
            textDecoration: 'none',
            fontWeight: 500,
            '&:hover': {
              color: 'var(--ar-accent-hover)',
              textDecoration: 'none',
            },
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          // No minHeight: design-system.css declared 34px as the one property
          // in that block without !important, so it always lost to MUI's own
          // sizing and 34px was never what shipped. Carried over as-rendered
          // rather than as-written.
          root: {
            borderRadius: 'var(--ar-radius)',
            textTransform: 'none',
            fontWeight: 550,
            letterSpacing: 0,
            boxShadow: 'none',
          },
          contained: {
            backgroundColor: 'var(--ar-accent)',
            color: 'var(--ar-on-accent)',
            '&:hover': { backgroundColor: 'var(--ar-accent-hover)' },
          },
        },
      },
      // `selected` cannot be overridden as a rule of its own — MUI raises "The
      // MuiTab component increases the CSS specificity of the `selected`
      // internal state" and ignores it, which it had been doing silently until
      // CI surfaced the warning. The state class nested under root is the form
      // that takes.
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 600,
            '&.Mui-selected': { color: 'var(--ar-fg)' },
          },
        },
      },
      // MUI tabs appear outside the entity page — TechDocs, TabbedCard and the
      // like — and were still drawing the stock indicator. Colourised with the
      // same brand gradient the BUI entity tabs use, so the two agree.
      MuiTabs: {
        styleOverrides: {
          indicator: {
            height: 2,
            borderRadius: '2px 2px 0 0',
            backgroundColor: 'transparent',
            backgroundImage: `linear-gradient(90deg, ${c.accent2} 0%, ${c.accent} 100%)`,
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            backgroundColor: 'var(--ar-surface-3)',
            borderRadius: 4,
            height: 21,
            fontSize: '0.75rem',
            color: 'var(--ar-fg-2)',
          },
        },
      },

      // ------------------------------------------------------------------
      // Backstage components.
      //
      // These MUST live here, not in design-system.css. Backstage's own
      // components style themselves with makeStyles({ name }), and MUI's JSS
      // generator only emits readable class names (BackstageSidebarItem-root-1)
      // in development — a production build emits `jss4-226`. Every
      // [class*='Backstage…'] selector therefore matches in `yarn start` and
      // matches nothing at all in the deployed image.
      //
      // Theme overrides are keyed on the component's `name` rather than its
      // generated class, so they survive minification. Mui* global class names
      // and BUI's CSS-Module prefixes are both stable, so those rules stay in
      // CSS.
      //
      // `!important` appears here because several of these have to beat a
      // sibling rule in the same stylesheet — most importantly `open`, which
      // sets width: drawerWidthOpen AFTER `root` and would otherwise win.
      // ------------------------------------------------------------------
      BackstageSidebar: {
        styleOverrides: {
          // No width here: Backstage sizes the drawer from sidebarConfig —
          // drawerWidthOpen (224, = RAIL_WIDTH) when pinned, 72 when not —
          // and pads the page to match, phone layout included. Forcing 224
          // made an unpinned rail a wide strip of unlabelled icons.
          drawer: {
            alignItems: 'stretch !important',
            borderRight: `1px solid ${c.border}`,
            // Dividers are styled from here, as a descendant, rather than
            // through a BackstageSidebarDivider override — that one does not
            // take, and centring the drawer collapses the <hr> to 2x2 because
            // it has no content to be sized by. Targeting the element keeps it
            // independent of any generated class name.
            '& hr': {
              width: '100% !important',
              height: '1px !important',
              alignSelf: 'stretch',
              border: 'none',
              // Ships with #383838 baked into the component — a hardcoded grey,
              // not a theme value, so it reads as a black bar on light.
              backgroundColor: `${c.border} !important`,
            },
          },
        },
      },
      BackstageHeader: {
        styleOverrides: {
          // Background and text colour come from pageTheme (genPageTheme);
          // layout only here.
          header: {
            boxShadow: 'none',
            padding: '26px 24px 22px',
            // Bleed RIGHT only. Measured on the deployed page: the content
            // column starts at x=56 with a 24px gutter, so a symmetric
            // `0 -24px` pushed the band to x=32 — underneath the 56px rail —
            // and dragged the title out to x=56, flush against it. With no
            // left bleed the band starts at the rail edge and the 24px padding
            // puts the title at x=80, level with the search field and cards.
            margin: '0 -24px 0 0',
            borderBottom: 'none',
          },
          title: {
            fontSize: '1.5rem',
            fontWeight: 650,
            letterSpacing: '-0.021em',
          },
          subtitle: { fontSize: '0.875rem', marginTop: 2 },
        },
      },
      BackstageContent: {
        styleOverrides: {
          root: { background: 'transparent', paddingTop: 22 },
        },
      },
      BackstageSelect: {
        styleOverrides: {
          formControl: { marginBottom: 0 },
          label: MICRO_LABEL,
        },
      },
      // ------------------------------------------------------------------
      // Catalog page furniture.
      //
      // These were written as [class*='CatalogReact…'] / [class*='PluginCatalog…']
      // rules in design-system.css and matched nothing in production: those are
      // JSS component names, which minify to `jss4-…`. Re-expressed here as
      // styleOverrides, keyed on the component name, which is the form that
      // survives. Rule names come from each plugin's published ClassKey types.
      // ------------------------------------------------------------------

      ...UNTYPED_OVERRIDES,

      // Personal / group picker: a plain list, not a card-in-a-card.
      CatalogReactUserListPicker: {
        styleOverrides: {
          root: {
            width: '100%',
            maxWidth: 'none',
            border: '1px solid var(--ar-border-soft)',
            borderRadius: 'var(--ar-radius-lg)',
            overflow: 'hidden',
          },
          title: MICRO_LABEL,
          groupWrapper: {
            border: 'none',
            borderRadius: 0,
            width: '100%',
            margin: 0,
            background: 'transparent',
            // Breathing room between groups (PERSONAL / ANSWERROCKET).
            '&:not(:first-child)': {
              borderTop: '1px solid var(--ar-border-soft)',
              marginTop: 6,
            },
          },
          menuItem: {
            width: '100%',
            boxSizing: 'border-box',
            padding: '8px 12px',
            borderRadius: 0,
            '&:hover': { backgroundColor: 'var(--ar-surface-2)' },
            // Selected row: accent tint, not a grey slab.
            '&[class*="Mui-selected"], &[aria-selected="true"]': {
              backgroundColor: 'var(--ar-accent-soft)',
              boxShadow: 'inset 2px 0 0 var(--ar-accent)',
            },
            // Label was pinned to ~32px and truncated ("Own", "Star").
            '& [class*="MuiListItemText-root"]': {
              flex: '1 1 auto',
              width: 'auto',
              minWidth: 0,
              margin: '0 8px 0 0',
            },
            '& [class*="MuiListItemText-root"] span': {
              fontSize: '0.875rem',
              whiteSpace: 'nowrap',
              overflow: 'visible',
              textOverflow: 'clip',
            },
          },
          listIcon: { minWidth: 26, color: 'var(--ar-fg-3)' },
        },
      },

      // Search field in the table toolbar: flat, no underline. The `input` rule
      // lands on the InputBase itself, so these apply directly rather than
      // through a descendant selector; `&&` clears MuiInput-underline.
      CatalogReactEntitySearchBar: {
        styleOverrides: {
          input: {
            '&&': {
              backgroundColor: 'var(--ar-surface-2)',
              border: '1px solid var(--ar-border)',
              borderRadius: 'var(--ar-radius)',
              padding: '2px 10px',
              fontSize: '0.875rem',
              minHeight: 34,
            },
            '&:before, &:after': { display: 'none' },
          },
        },
      },

      BackstageSelectInputBase: {
        styleOverrides: {
          root: {
            backgroundColor: c.surface,
            border: `1px solid ${c.border}`,
            borderRadius: 6,
            minHeight: 34,
            fontSize: '0.875rem',
            '&:hover': { borderColor: c.borderStrong },
          },
        },
      },
      BackstageEmptyState: {
        styleOverrides: {
          // Ships as a light illustration; dim it so it does not glare.
          root: { '& img, & svg': { opacity: 0.16, filter: 'grayscale(1)' } },
        },
      },
    },
  });
};

const answerRocketDark = buildTheme('dark', DARK);
const answerRocketLight = buildTheme('light', LIGHT);

const darkTheme = ThemeBlueprint.make({
  name: 'answerrocket-dark',
  params: {
    theme: {
      id: 'answerrocket-dark',
      title: 'AnswerRocket Dark',
      variant: 'dark',
      icon: <Brightness2Icon />,
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={answerRocketDark} children={children} />
      ),
    },
  },
});

const lightTheme = ThemeBlueprint.make({
  name: 'answerrocket-light',
  params: {
    theme: {
      id: 'answerrocket-light',
      title: 'AnswerRocket Light',
      variant: 'light',
      icon: <Brightness7Icon />,
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={answerRocketLight} children={children} />
      ),
    },
  },
});

export const themeModule = createFrontendModule({
  pluginId: 'app',
  extensions: [darkTheme, lightTheme],
});
